/**
 * Zahlungen erfassen und offenen Rechnungen zuordnen.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { formatAmount, formatDate, isOverdue, today } from '@erp/shared';

import { ApiError, api, withQuery } from '../api/client';
import { useAuth } from '../auth';
import { PageHeader } from '../components/Layout';
import { useToast } from '../components/toast';
import {
  Alert,
  Badge,
  Dialog,
  EmptyState,
  Field,
  LoadingBlock,
  NumericInput,
  Select,
  TextInput,
} from '../components/ui';

type OpenItem = {
  id: string;
  type: string;
  number: string | null;
  externalReference: string | null;
  documentDate: string;
  dueDate: string | null;
  grossTotal: number;
  paidAmount: number;
  partnerId: string;
  partnerName: string;
  partnerNumber: string;
};

type PaymentRow = {
  id: string;
  paymentDate: string;
  direction: 'incoming' | 'outgoing';
  amount: number;
  method: string;
  reference: string | null;
  unallocatedAmount: number;
  partnerName: string | null;
};

export function PaymentsPage() {
  const { can } = useAuth();
  const [side, setSide] = useState<'sales' | 'purchase'>('sales');
  const [payFor, setPayFor] = useState<OpenItem | null>(null);

  const openItems = useQuery({
    queryKey: ['open-items', side],
    queryFn: () =>
      api.get<{ items: OpenItem[]; openTotal: number; overdueTotal: number }>(
        withQuery('/api/accounting/open-items', { side }),
      ),
  });

  const payments = useQuery({
    queryKey: ['payments'],
    queryFn: () =>
      api.get<{ items: PaymentRow[]; total: number }>(
        withQuery('/api/accounting/payments', { limit: 50 }),
      ),
  });

  return (
    <>
      <PageHeader title="Zahlungen" subtitle="Offene Posten und erfasste Zahlungen" />

      <div className="row" style={{ marginBottom: 16, gap: 6 }}>
        <button
          type="button"
          className={`btn btn-sm${side === 'sales' ? ' btn-primary' : ''}`}
          onClick={() => setSide('sales')}
        >
          Forderungen
        </button>
        <button
          type="button"
          className={`btn btn-sm${side === 'purchase' ? ' btn-primary' : ''}`}
          onClick={() => setSide('purchase')}
        >
          Verbindlichkeiten
        </button>
      </div>

      {openItems.data ? (
        <div className="tile-grid" style={{ marginBottom: 16 }}>
          <div className="tile">
            <div className="tile-label">Offen insgesamt</div>
            <div className="tile-value">{formatAmount(openItems.data.openTotal)} €</div>
          </div>
          <div className="tile">
            <div className="tile-label">Davon überfällig</div>
            <div
              className="tile-value"
              style={openItems.data.overdueTotal > 0 ? { color: 'var(--danger)' } : undefined}
            >
              {formatAmount(openItems.data.overdueTotal)} €
            </div>
          </div>
        </div>
      ) : null}

      <div className="card">
        <div className="card-header">
          <h2>Offene Posten</h2>
        </div>

        {openItems.isLoading ? <LoadingBlock /> : null}

        {openItems.data ? (
          openItems.data.items.length === 0 ? (
            <EmptyState title="Keine offenen Posten" />
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th className="shrink">Beleg</th>
                    <th className="shrink">Datum</th>
                    <th>Partner</th>
                    <th className="shrink">Fällig</th>
                    <th className="numeric">Betrag</th>
                    <th className="numeric">Bezahlt</th>
                    <th className="numeric">Offen</th>
                    {can('accounting:write') ? <th className="shrink"></th> : null}
                  </tr>
                </thead>
                <tbody>
                  {openItems.data.items.map((item) => {
                    const open = item.grossTotal - item.paidAmount;
                    const overdue = item.dueDate ? isOverdue(item.dueDate) : false;

                    return (
                      <tr key={item.id}>
                        <td className="mono">{item.number ?? item.externalReference}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatDate(item.documentDate)}</td>
                        <td>{item.partnerName}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {overdue ? (
                            <Badge kind="danger">{formatDate(item.dueDate)}</Badge>
                          ) : (
                            formatDate(item.dueDate)
                          )}
                        </td>
                        <td className="numeric">{formatAmount(item.grossTotal)}</td>
                        <td className="numeric muted">
                          {item.paidAmount > 0 ? formatAmount(item.paidAmount) : ''}
                        </td>
                        <td className="numeric" style={{ fontWeight: 600 }}>
                          {formatAmount(open)}
                        </td>
                        {can('accounting:write') ? (
                          <td>
                            <button
                              type="button"
                              className="btn btn-sm"
                              onClick={() => setPayFor(item)}
                            >
                              Zahlung erfassen
                            </button>
                          </td>
                        ) : null}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        ) : null}
      </div>

      <div className="card">
        <div className="card-header">
          <h2>Erfasste Zahlungen</h2>
        </div>
        {payments.data ? (
          payments.data.items.length === 0 ? (
            <EmptyState title="Noch keine Zahlungen erfasst" />
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th className="shrink">Datum</th>
                    <th className="shrink">Richtung</th>
                    <th>Partner</th>
                    <th>Verwendungszweck</th>
                    <th className="numeric">Betrag</th>
                    <th className="numeric">Nicht zugeordnet</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.data.items.map((payment) => (
                    <tr key={payment.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{formatDate(payment.paymentDate)}</td>
                      <td>
                        <Badge kind={payment.direction === 'incoming' ? 'success' : 'neutral'}>
                          {payment.direction === 'incoming' ? 'Eingang' : 'Ausgang'}
                        </Badge>
                      </td>
                      <td>{payment.partnerName ?? ''}</td>
                      <td className="muted">{payment.reference ?? ''}</td>
                      <td className="numeric">{formatAmount(payment.amount)}</td>
                      <td className="numeric">
                        {payment.unallocatedAmount > 0 ? (
                          <span style={{ color: 'var(--warning)' }}>
                            {formatAmount(payment.unallocatedAmount)}
                          </span>
                        ) : (
                          ''
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : null}
      </div>

      {payFor ? (
        <PaymentDialog
          item={payFor}
          direction={side === 'sales' ? 'incoming' : 'outgoing'}
          onClose={() => setPayFor(null)}
        />
      ) : null}
    </>
  );
}

function PaymentDialog({
  item,
  direction,
  onClose,
}: {
  item: OpenItem;
  direction: 'incoming' | 'outgoing';
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const open = item.grossTotal - item.paidAmount;

  const [paymentDate, setPaymentDate] = useState(today());
  const [amount, setAmount] = useState(open);
  const [discount, setDiscount] = useState(0);
  const [method, setMethod] = useState<'bank' | 'cash' | 'card' | 'paypal' | 'other'>('bank');
  const [reference, setReference] = useState(item.number ?? '');
  const [error, setError] = useState<string | null>(null);

  const remaining = useMemo(() => open - amount - discount, [open, amount, discount]);

  const save = useMutation({
    mutationFn: () =>
      api.post('/api/accounting/payments', {
        paymentDate,
        direction,
        amount,
        method,
        partnerId: item.partnerId,
        reference,
        allocations: [{ documentId: item.id, amount, discountAmount: discount }],
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['open-items'] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['journal'] });
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      toast.success('Die Zahlung wurde erfasst und gebucht.');
      onClose();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Die Zahlung konnte nicht erfasst werden.'),
  });

  return (
    <Dialog
      title={`Zahlung zu ${item.number ?? item.externalReference}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={save.isPending || amount <= 0}
            onClick={() => {
              setError(null);
              save.mutate();
            }}
          >
            Erfassen und buchen
          </button>
        </>
      }
    >
      {error ? <Alert kind="error">{error}</Alert> : null}

      <div className="definition-list" style={{ marginBottom: 16 }}>
        <dt>Partner</dt>
        <dd>{item.partnerName}</dd>
        <dt>Rechnungsbetrag</dt>
        <dd className="mono">{formatAmount(item.grossTotal)} €</dd>
        <dt>Noch offen</dt>
        <dd className="mono">
          <strong>{formatAmount(open)} €</strong>
        </dd>
      </div>

      <div className="form-grid">
        <Field label="Zahlungsdatum" span={6} required>
          <TextInput type="date" value={paymentDate} onChange={setPaymentDate} />
        </Field>
        <Field label="Zahlungsweg" span={6}>
          <Select
            value={method}
            onChange={setMethod}
            options={[
              { value: 'bank', label: 'Bank' },
              { value: 'cash', label: 'Kasse' },
              { value: 'card', label: 'Karte' },
              { value: 'paypal', label: 'PayPal' },
              { value: 'other', label: 'Sonstiges' },
            ]}
          />
        </Field>
        <Field label="Gezahlter Betrag" span={6} required>
          <NumericInput kind="amount" value={amount} onChange={setAmount} />
        </Field>
        <Field
          label="Davon Skonto"
          span={6}
          hint="Wird als Erlösschmälerung gebucht"
        >
          <NumericInput kind="amount" value={discount} onChange={setDiscount} />
        </Field>
        <Field label="Verwendungszweck" span={12}>
          <TextInput value={reference} onChange={setReference} />
        </Field>
      </div>

      {remaining > 0 ? (
        <Alert kind="warning">
          Nach dieser Zahlung bleiben {formatAmount(remaining)} € offen — die Rechnung gilt als
          teilweise bezahlt.
        </Alert>
      ) : remaining < 0 ? (
        <Alert kind="error">
          Der zugeordnete Betrag übersteigt den offenen Posten um {formatAmount(-remaining)} €.
        </Alert>
      ) : (
        <Alert kind="success">Die Rechnung wird damit vollständig ausgeglichen.</Alert>
      )}
    </Dialog>
  );
}
